// sandbox — the PlaySandbox game driver. Holds a fixed seed + an append-only move log and replays
// the whole game by folding `reduce` over `initialState(seed)`. This makes save→reflect trivial:
// when a rule changes, `rebuild(newRuleSet)` re-instantiates the engine view and re-folds the SAME
// intents against the new rules (spec §6.1 PlaySandbox: "現局へ新ルール適用 (合法手再計算)" / "同 seed で再開").
//
// The engine reducer is the single authority (the same code the future TableDO runs), so the
// sandbox is a faithful play-test, not a separate simulation.
import type { GameState, Intent, RuleSet, Seat, TileId, YakuDefMeta } from '@mj/protocol';
import {
  initialState,
  reduce,
  legalMoves,
  exhaustiveDraw,
  YAKU_CATALOG,
  type GameEvent,
} from '@mj/engine';

// GameState is plain JSON data (TileId strings, numbers, arrays) — structuredClone is exact and keeps
// the sandbox from aliasing the engine's internal state when we hand it to a mutating edge resolver.
function cloneState(s: GameState): GameState {
  return structuredClone(s);
}

export interface ScriptedMove {
  seat: Seat;
  intent: Intent;
}

export interface SandboxStep {
  state: GameState;
  events: GameEvent[];
  move: ScriptedMove;
}

export interface RebuildResult {
  state: GameState;
  events: GameEvent[];
  /** Index of the first move that the engine rejected (rules changed under it), or -1 if all applied. */
  rejectedAt: number;
  error?: string;
}

/** A seat that may legally act right now, with its legal intents. */
export interface ActableSeat {
  seat: Seat;
  moves: Intent[];
}

export class SandboxGame {
  readonly seed: Uint8Array;
  private rs: RuleSet;
  // The WORKING catalog used to score wins; custom (DSL) yaku live here so they fire in the sandbox.
  private catalog: YakuDefMeta[];
  private moves: ScriptedMove[] = [];
  private _state: GameState;
  private _events: GameEvent[] = [];

  constructor(rs: RuleSet, seed: Uint8Array, catalog: YakuDefMeta[] = YAKU_CATALOG) {
    this.rs = rs;
    this.catalog = catalog;
    this.seed = seed;
    this._state = initialState(rs, seed);
  }

  get state(): GameState {
    return this._state;
  }
  get eventLog(): GameEvent[] {
    return this._events;
  }
  get moveLog(): ScriptedMove[] {
    return this.moves.slice();
  }

  /** True when the live wall is exhausted in a draw phase: the hand must be finalized as a ryuukyoku
   *  (the reducer leaves edge-resolution to the caller; see finalizeExhaustive). */
  needsExhaustiveDraw(): boolean {
    const s = this._state;
    return (s.phase === 'draw' || s.phase === 'discard') && s.wall.length === 0;
  }

  isTerminal(): boolean {
    return this._state.phase === 'resolve' || this._state.phase === 'end';
  }

  /** Seats that can legally act in the current phase (turn seat, or all callers in a call window). */
  actableSeats(): ActableSeat[] {
    const s = this._state;
    if (s.phase === 'end' || s.phase === 'resolve') return [];
    if (this.needsExhaustiveDraw()) return []; // nothing to act; caller finalizes the draw.
    const out: ActableSeat[] = [];
    if (s.phase === 'callWindow') {
      for (let seat = 0; seat < s.players.length; seat++) {
        if (s.lastDiscard && s.lastDiscard.seat === seat) continue;
        const moves = legalMoves(s, seat as Seat);
        if (moves.length > 0) out.push({ seat: seat as Seat, moves });
      }
      return out;
    }
    // draw/discard: only the seat on turn acts.
    const moves = legalMoves(s, s.turn);
    if (moves.length > 0) out.push({ seat: s.turn, moves });
    return out;
  }

  /** Resolve an exhausted wall into a ryuukyoku (荒牌平局 / nagashi-mangan) via the engine edges. */
  finalizeExhaustive(): { events: GameEvent[] } {
    const r = exhaustiveDraw(cloneState(this._state), this.rs);
    this._state = r.state;
    this._events = r.events;
    return { events: r.events };
  }

  /** Apply one scripted move, appending it to the log on success. Returns the step or an error. */
  apply(seat: Seat, intent: Intent): { ok: true; step: SandboxStep } | { ok: false; error: string; code: string } {
    const r = reduce(this._state, seat, intent, this.rs, this.catalog);
    if ('error' in r) return { ok: false, error: r.error, code: r.code };
    this._state = r.state;
    this._events = r.events;
    this.moves.push({ seat, intent });
    return { ok: true, step: { state: r.state, events: r.events, move: { seat, intent } } };
  }

  /**
   * Re-instantiate the engine from the seed and re-fold every scripted move against `rs`. Stops at
   * the first move the (possibly changed) rules reject, truncating the log there. This is the
   * save→reflect path: the open game re-evaluates under the new RuleSet without losing scripted play.
   */
  rebuild(rs: RuleSet, catalog: YakuDefMeta[] = this.catalog): RebuildResult {
    this.rs = rs;
    this.catalog = catalog;
    let state = initialState(rs, this.seed);
    let events: GameEvent[] = [];
    const kept: ScriptedMove[] = [];
    for (let i = 0; i < this.moves.length; i++) {
      const m = this.moves[i]!;
      const r = reduce(state, m.seat, m.intent, rs, catalog);
      if ('error' in r) {
        this.moves = kept;
        this._state = state;
        this._events = events;
        return { state, events, rejectedAt: i, error: r.error };
      }
      state = r.state;
      events = r.events;
      kept.push(m);
    }
    this.moves = kept;
    this._state = state;
    this._events = events;
    return { state, events, rejectedAt: -1 };
  }

  /** Reset to the opening deal (same seed), discarding the move log. */
  reset(rs: RuleSet = this.rs, catalog: YakuDefMeta[] = this.catalog): void {
    this.rs = rs;
    this.catalog = catalog;
    this.moves = [];
    this._state = initialState(rs, this.seed);
    this._events = [];
  }
}

// ---------------------------------------------------------------------------
// Auto-play policy — a TRIVIAL, deterministic chooser for "step"/"auto" (NOT a playing AI; spec is
// explicit that no game AI exists). It just picks a legal move so the owner can watch a hand play to
// a conclusion: take a win if offered, else pass calls, else discard the last (drawn) tile.
// ---------------------------------------------------------------------------
export function autoChoose(moves: Intent[]): Intent {
  const win = moves.find((m) => m.kind === 'tsumo' || m.kind === 'ron');
  if (win) return win;
  const pass = moves.find((m) => m.kind === 'pass');
  if (pass) return pass;
  // Prefer a tsumogiri-style discard of the last legal discard (kept deterministic).
  const discards = moves.filter((m) => m.kind === 'discard');
  if (discards.length > 0) return discards[discards.length - 1]!;
  // Otherwise take the first non-call structural move (kan/kyuushuu) — rare.
  return moves[0]!;
}

/** Make a deterministic 32-byte seed from a small integer (rig-a-deal). Single-sourced in @mj/protocol
 *  so the TableDO (server) derives byte-identical per-hand walls; re-exported here for existing callers. */
export { seedFromNumber } from '@mj/protocol';

export type { TileId };
